import React, { FC, useState } from 'react'
import classNames from 'classnames'
import { InView } from 'react-intersection-observer'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { AsH2, Text } from '@/atoms/Text'
import { AutoBookModal } from '@/organisms/AutoBookModal'
import { TheaterPreferencesModal, TheaterPreferencesProvider } from '@/organisms/TheaterPreferences'
import { useUser } from '@/services/UserService'
import { TheatricalReleaseObject, TheatricalShowtimeVenue } from '@/types/codegen-federation'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { EmptyStateGiveaway } from '@/views/TicketCheckoutViews/ShowtimesView/components/EmptyStateGiveaway'
import { VenueItem } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueItem'
import { GroupTicketsModalTypes } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal/GroupTicketsModal'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'
import { GuildTicketsBanner } from './banners/GuildTicketsBanner'

export interface LocaleWarningModalState {
  isOpen: boolean
  warning: string
  callback: () => void
}

interface VenueListProps {
  venues: TheatricalShowtimeVenue[]
  theatricalRelease: TheatricalReleaseObject
  theaterSlug: string
  reservationId?: string
  className?: string
  isGroupTicketsModalOpen: boolean
  isBuyoutModalOpen: boolean
  openGroupTicketsModal: (type: GroupTicketsModalTypes) => void
  openBuyoutModal: (buyoutVenue: TheatricalShowtimeVenue, theaterChain: string) => void
  setLocaleWarningModalState: React.Dispatch<React.SetStateAction<LocaleWarningModalState>>
  theatricalSlug: string
  showGuildTicketBanner: boolean
  showGiveawayEmptyState: boolean
}

const VenueList: FC<VenueListProps> = ({
  venues,
  theatricalRelease,
  theaterSlug,
  reservationId,
  isGroupTicketsModalOpen,
  openBuyoutModal,
  openGroupTicketsModal,
  setLocaleWarningModalState,
  isBuyoutModalOpen,
  className,
  theatricalSlug,
  showGuildTicketBanner,
  showGiveawayEmptyState,
}) => {
  const [isAutoBookModalOpen, setIsAutoBookModalOpen] = useState(false)
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false)
  const { query } = useSafeRouter()
  const isEmpty = venues?.length === 0
  const { trackProductViewed } = useEcommerceEvents()
  const { isLoggedIn } = useUser()
  const { areGuildTicketsAvailable, discountCodes } = useGuildTickets(theatricalSlug)
  const [disableShowtimes, setDisableShowtimes] = useState(false)

  if (isEmpty)
    return <VenueListEmptyState theatricalSlug={theatricalSlug} showGiveawayEmptyState={showGiveawayEmptyState} />

  return (
    <>
      <TheaterPreferencesProvider>
        <InView
          as="ul"
          className={classNames('flex flex-col gap-2', className)}
          triggerOnce
          onChange={(inView) => {
            if (!inView) return

            trackProductViewed({
              category: 'theatrical ticket',
              funnel: 'theatrical',
              sku: `ticket-${theaterSlug}`,
              product_id: `ticket-${theaterSlug}`,
              name: `${theaterSlug} theatrical ticket`,
              price: 15,
              currency: 'USD',
              quantity: 1,
              projectSlug: theaterSlug,
            })
          }}
        >
          {venues.map((venue, idx) => {
            const showGuildBanner = idx === 0 && venues.length > 1 && areGuildTicketsAvailable && isLoggedIn

            return (
              <React.Fragment key={venue.id}>
                <VenueItem
                  key={venue.id}
                  theaterSlug={theaterSlug}
                  reservationId={reservationId}
                  query={query}
                  theatricalRelease={theatricalRelease}
                  venue={venue}
                  setLocaleWarningModalState={setLocaleWarningModalState}
                  isGroupTicketsModalOpen={isGroupTicketsModalOpen}
                  openGroupTicketsModal={openGroupTicketsModal}
                  handleOpenAutoBookModal={() => setIsAutoBookModalOpen(true)}
                  handleOpenPreferencesModal={() => setIsPreferencesModalOpen(true)}
                  isBuyoutModalOpen={isBuyoutModalOpen}
                  openBuyoutModal={openBuyoutModal}
                  zIndex={venues.length - idx}
                  showtimesDisabled={disableShowtimes}
                  setDisableShowtimes={setDisableShowtimes}
                />
                {showGuildBanner && showGuildTicketBanner && (
                  <FlexColumn className="w-full items-center gap-6 text-center md:hidden">
                    <GuildTicketsBanner
                      shouldUseGradient
                      theatricalSlug={theatricalSlug}
                      projectName={theatricalRelease?.title ?? ''}
                      discountCodes={discountCodes}
                      className="relative z-10 w-full overflow-hidden rounded-2xl md:hidden"
                      mobileClassName="text-left z-[11]"
                    />
                  </FlexColumn>
                )}
              </React.Fragment>
            )
          })}
          <AutoBookModal open={isAutoBookModalOpen} onClose={() => setIsAutoBookModalOpen(false)} />
          <TheaterPreferencesModal isOpen={isPreferencesModalOpen} onClose={() => setIsPreferencesModalOpen(false)} />
        </InView>
      </TheaterPreferencesProvider>
    </>
  )
}
export default VenueList

const VenueListEmptyState = ({
  theatricalSlug,
  showGiveawayEmptyState,
}: {
  theatricalSlug: string
  showGiveawayEmptyState: boolean
}) => {
  const { t } = useTranslate('tickets')
  const { isActive, isReady } = useGiveawaySchedule(theatricalSlug)
  if (!isReady) return null

  if (isActive && showGiveawayEmptyState) {
    return <EmptyStateGiveaway theatricalSlug={theatricalSlug} />
  }

  return (
    <FlexColumn className="my-6 items-center gap-6 text-center">
      <Image
        priority
        className="md:!h-[136px] md:!w-[180px]"
        alt=""
        src="/v1712858189/share-seats-illustration-colored-02_2x.webp"
        width={120}
        height={91}
      />
      <FlexColumn className="gap-2">
        <Text as={AsH2} className="photon-title-sm md:photon-title-md font-semibold" weight="bold">
          {t('noShowtimesAvailable', 'No Showtimes Available')}
        </Text>
        <Text className="photon-paragraph-sm md:photon-paragraph-md max-w-lg" color="core-gray-700">
          {t(
            'atTheMomentTheresNoShowtimesAvailable',
            "At the moment, there aren't any showtimes in your area. Feel free to explore other locations or try another date!",
          )}
        </Text>
      </FlexColumn>
    </FlexColumn>
  )
}
